import { FC, useMemo } from 'react';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import dayjs, { Dayjs } from 'dayjs';

import DateRangePicker from '../../../components/DateRangePicker';
import { PickersShortcutsItem } from '@mui/x-date-pickers';

const getDefaultDateRanges: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
        label: 'This Month',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('month'), today];
        },
    },
    {
        label: 'This Quarter',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('quarter'), today];
        },
    },
    {
        label: 'Last Month',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(1, 'month').startOf('month'), today.subtract(1, 'month').endOf('month')];
        },
    },
    {
        label: 'Last Quarter',
        getValue: () => {
            const today = dayjs();
            const lastQuarter = today.quarter() === 1 ? 4 : today.quarter() - 1;
            return [today.quarter(lastQuarter).startOf('quarter'), today.quarter(lastQuarter).endOf('quarter')];
        },
    },
    {
        label: 'Last 30 Days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(30, 'days'), today];
        },
    },
    {
        label: 'Last 60 Days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(60, 'days'), today];
        },
    },
    {
        label: 'Last 90 Days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(90, 'days'), today];
        },
    },
    {
        label: 'Year to Date',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('year'), today];
        },
    },
];

const dateRanges = getDefaultDateRanges;

export type HatCriteriaDataRangePickerProps = {
    label: string;
    onChange(startDate?: Date | null, endDate?: Date | null): void;
    startDate?: Date | null;
    endDate?: Date | null;
    minDate?: Date;
    maxDate?: Date;
};

const HatCriteriaDataRangePicker: FC<HatCriteriaDataRangePickerProps> = (props): React.JSX.Element => {
    const { label, onChange, startDate, endDate } = props;

    const handleDateRangeChange = (newDateRange: DateRange<Dayjs>): void => {
        if (newDateRange[0] && newDateRange[1]) {
            onChange(newDateRange[0].toDate(), newDateRange[1].toDate());
        } else {
            onChange(null, null);
        }
    };

    const memoedValue = useMemo(() => {
        return [startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null] as DateRange<Dayjs>;
    }, [startDate, endDate]);

    return (
        <DateRangePicker
            label={label}
            ranges={dateRanges}
            disableFuture={true}
            value={memoedValue}
            onChange={handleDateRangeChange}
        />
    );
};

export default HatCriteriaDataRangePicker;
